import { useEffect, useMemo } from 'react'
import { Logger } from '@/components/Logger'
import { useGetUserSelf } from '@/hooks/v1'
import { getAllAssetPricesQueryKey } from '@/hooks/v1/hooks/useGetAllAssetPrices'
import { getAssetPriceQueryKey } from '@/hooks/v1/hooks/useGetAssetPrice'
import { getAssetYTMByIdQueryKey } from '@/hooks/v1/hooks/useGetAssetYTMById'
import type { AssetPrice } from '@/hooks/v1/types/AssetPrice'
import { queryClient } from '@/services/api'
import { subscribeRealtime } from '@/services/api/realtimeSubscription'
import { isWebSocketErrorMessage } from './useWebSocketStatus'

type UsePriceChangedSubscriptionOptions = {
  enabled?: boolean
}

export const usePriceChangedSubscription = ({
  enabled = true,
}: UsePriceChangedSubscriptionOptions = {}) => {
  const { data: userSelfData } = useGetUserSelf()
  const userId = useMemo(() => userSelfData?.data?.id ?? null, [userSelfData])

  useEffect(() => {
    // Only subscribe if user is authenticated and has a uid
    if (!enabled || !userId) {
      return
    }

    // Connect to WebSocket using centralized client
    const unsubscribe = subscribeRealtime(
      {
        onMessage: (data) => {
          try {
            // Track data update for connection health monitoring

            // Check if message contains an error before processing
            if (isWebSocketErrorMessage(data)) {
              Logger.error('DEBUG WS PRICES: Received error message, skipping setQueryData:', data)
              return
            }

            const updates: AssetPrice[] = Array.isArray(data)
              ? data
              : data && typeof data === 'object'
                ? Object.values(data)
                : []

            if (!updates.length) {
              return
            }

            const queryKey = getAllAssetPricesQueryKey()
            queryClient.setQueryData(queryKey, (previous: any) => {
              const currentData: AssetPrice[] = Array.isArray(previous?.data) ? previous.data : []

              const merged = new Map<string, AssetPrice>()
              currentData.forEach((price) => {
                if (price?.asset_id) {
                  merged.set(price.asset_id, price)
                }
              })
              updates.forEach((price) => {
                if (price?.asset_id) {
                  const previousPrice = merged.get(price.asset_id)
                  merged.set(price.asset_id, {
                    ...(previousPrice ?? {}),
                    ...price,
                    ytm: price.ytm ?? previousPrice?.ytm,
                  })
                }
              })

              return {
                ...(previous ?? {}),
                data: Array.from(merged.values()),
              }
            })

            updates.forEach((price) => {
              if (!price?.asset_id) {
                return
              }

              queryClient.setQueryData(
                getAssetPriceQueryKey({ path: { asset_id: price.asset_id } }),
                (previous: any) => ({
                  ...(previous ?? {}),
                  data: price,
                }),
              )

              if (price.ytm !== undefined) {
                queryClient.setQueryData(
                  getAssetYTMByIdQueryKey({ path: { asset_id: price.asset_id } }),
                  (previous: any) => ({
                    ...(previous ?? {}),
                    data: {
                      asset_id: price.asset_id,
                      current_price: price.price ?? '0',
                      current_time: price.time,
                      yield_to_maturity: price.ytm,
                    },
                  }),
                )
              }
            })
          } catch (_error) {
            //Logger.error('DEBUG WS PRICES: Error parsing WebSocket message:', error)
          }
        },
        onOpen: () => {
          Logger.log('DEBUG WS PRICES: WebSocket connected successfully to asset prices stream')
        },
        onError: (error) => {
          Logger.error('DEBUG WS PRICES: WebSocket error:', error)
        },
        onClose: (event) => {
          Logger.log(
            'DEBUG WS PRICES: WebSocket connection closed, code:',
            event.code,
            'wasClean:',
            event.wasClean,
          )
        },
      },
      {
        key: 'prices:all',
        path: '/prices',
        subscribeData: { subscribe_all: true },
        unsubscribeData: { unsubscribe_all: true },
        mapNotification: (data) =>
          data && typeof data === 'object' && 'prices' in data
            ? (data as { prices: unknown }).prices
            : data,
      },
    )

    // Return cleanup function
    return () => {
      unsubscribe()
    }
  }, [enabled, userId])
}
